import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config'; 
import { 
    doc, 
    updateDoc, 
    serverTimestamp 
} from 'firebase/firestore';

const RefacturarPoliza = ({ show, onClose, poliza, onSuccess }) => {
    // 1. ESTADOS LOCALES
    const [activeId, setActiveId] = useState(null);
    const [fechaRefacturacion, setFechaRefacturacion] = useState(''); 
    const [isSaving, setIsSaving] = useState(false);

    // Sincronización al detectar cambio de póliza
    if (poliza && poliza.id !== activeId) {
        setActiveId(poliza.id);
        setFechaRefacturacion(''); 
    }

    // Limpieza al cerrar
    useEffect(() => {
        if (!show) {
            setActiveId(null);
            setFechaRefacturacion('');
        }
    }, [show]);

    const handleSave = async (e) => {
        e.preventDefault();
        if (isSaving || !fechaRefacturacion) return;
        
        setIsSaving(true);
        try {
            const polizaRef = doc(db, poliza.path);
            
            const updateData = {
                fechaRefacturacion: fechaRefacturacion,
                estadoGestionRenovacion: "REFACTURACION",
                fechaUltimaGestion: serverTimestamp(),
                bloqueo: null 
            };

            await updateDoc(polizaRef, updateData);

            if (onSuccess) {
                onSuccess('Refacturación programada correctamente', { ...poliza, ...updateData });
            }
            onClose();
        } catch (error) {
            console.error("Error al guardar refacturación:", error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!show || !poliza) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden animate-fade-in border border-gray-100">
                
                {/* Header */}
                <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 bg-orange-50/50 shrink-0 text-left">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3 font-['Inter']">
                        <span className="bg-orange-100 text-orange-600 p-2 rounded-lg shadow-sm">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </span>
                        Refacturar Póliza
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-white shadow-sm">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-8 bg-white relative text-left">
                    <div className="space-y-8">
                        
                        {/* SECCIÓN: DATOS DE LA PÓLIZA (RECUADRO ALINEADO) */}
                        <fieldset className="border border-slate-200 rounded-xl p-5 relative bg-slate-50/30">
                            <legend className="px-2 text-[10px] font-black text-blue-500 bg-white absolute -top-2.5 left-4 tracking-[0.15em] uppercase">Póliza Seleccionada</legend>
                            <div className="pt-1">
                                <p className="text-lg font-black text-slate-800 tracking-tight">
                                    #{poliza.nPoliza} <span className="text-slate-300 mx-1">•</span> {poliza.compania}
                                </p>
                            </div>
                        </fieldset>

                        {/* SECCIÓN: GESTIÓN INTERNA */}
                        <fieldset className="border border-slate-200 rounded-xl p-5 relative bg-white">
                            <legend className="px-2 text-[10px] font-black text-slate-400 bg-white absolute -top-2.5 left-4 tracking-[0.15em] uppercase">Gestión interna</legend>
                            <div className="pt-2">
                                <div className="w-full">
                                    <label className="block text-[11px] font-bold text-slate-500 mb-2 uppercase tracking-wide">Nueva fecha de refacturación *</label>
                                    <input 
                                        type="date"
                                        value={fechaRefacturacion} 
                                        onChange={(e) => setFechaRefacturacion(e.target.value)} 
                                        className="w-full h-12 px-4 border border-slate-300 rounded-xl outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 bg-white font-bold text-slate-700 transition-all text-base"
                                        required
                                    />
                                    <p className="text-[10px] text-slate-400 mt-2 italic">* Esta fecha definirá cuándo aparecerá el caso en el tablero de gestión.</p>
                                </div>
                            </div>
                        </fieldset>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-5 border-t border-gray-100 flex justify-end gap-4 bg-gray-50 shrink-0">
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="px-6 py-2.5 rounded-xl border border-slate-300 text-slate-600 font-bold hover:bg-white hover:text-slate-800 transition shadow-sm text-xs uppercase tracking-widest active:scale-95"
                    >
                        Cancelar
                    </button>
                    <button 
                        onClick={handleSave} 
                        disabled={isSaving || !fechaRefacturacion} 
                        className="px-10 py-2.5 rounded-xl bg-orange-600 text-white font-bold hover:bg-orange-700 transition shadow-lg shadow-orange-200 disabled:opacity-50 disabled:shadow-none min-w-[160px] text-xs uppercase tracking-widest active:scale-95"
                    >
                        {isSaving ? 'Guardando...' : 'Confirmar'}
                    </button>
                </div>
            </div>
            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>,
        document.body
    );
};

export default RefacturarPoliza;